/**
 * Viewer-mode plumbing (P3.md "Viewer mode"): ONE capabilities object drives
 * the whole bundle. `canWrite === false` (share-key viewers) hides every
 * write affordance — SavePill, edit forms, add buttons, undo, Attention —
 * by consulting THIS context, never by a second fork of the components.
 *
 * The server's share shell (apps/server routes/share.ts) declares viewer
 * sessions by injecting `window.__TD_CAPABILITIES__` into the served page
 * (and its /s/<key>/ base path is a second, structural signal). The default —
 * plain app, signed-in session — is full write: the P1/P2 behavior unchanged.
 */
import { createContext, useContext } from "react";
import { shareKeyFromUrl } from "../sync/viewer.ts";

export interface Capabilities {
  readonly canWrite: boolean;
  /** Person the share key is scoped to (viewer sessions); null = owner/editor. */
  readonly personId: string | null;
}

export const OWNER_CAPABILITIES: Capabilities = Object.freeze({ canWrite: true, personId: null });

interface InjectedCapabilities {
  can_write?: unknown;
  person_id?: unknown;
}

/** Read the share-shell injection; absent = owner/editor (full write). */
export function detectCapabilities(): Capabilities {
  try {
    const injected = (globalThis as { __TD_CAPABILITIES__?: InjectedCapabilities }).__TD_CAPABILITIES__;
    if (injected !== undefined) {
      return {
        canWrite: injected.can_write === true, // declared sessions are explicit
        personId: typeof injected.person_id === "string" ? injected.person_id : null,
      };
    }
    // Structural fallback: a share key in the URL (`/s/<key>` or the v1
    // `/?person=<key>` / `?key=` forms) means a viewer session even without the
    // injection — fail CLOSED on every write affordance.
    if (shareKeyFromUrl() !== null) {
      return { canWrite: false, personId: null };
    }
    if (typeof location !== "undefined" && /^\/s\//.test(location.pathname)) {
      return { canWrite: false, personId: null };
    }
  } catch {
    /* non-browser (tests) — owner default */
  }
  return OWNER_CAPABILITIES;
}

const CapabilitiesContext = createContext<Capabilities>(OWNER_CAPABILITIES);

export const CapabilitiesProvider = CapabilitiesContext.Provider;

export function useCapabilities(): Capabilities {
  return useContext(CapabilitiesContext);
}
